import { MutationTree } from 'vuex';
import { DetectionRuleGroupState, DetectionRuleGroup } from './types';
import { DetectionRule } from '../detection_rule/types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// エラーをクリアする
const resetErrors = (state: DetectionRuleGroupState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const addRulesState = state.addRules;
    addRulesState.error = false;
    addRulesState.errorTitle = undefined;
    addRulesState.errorDetails = undefined;

    const removeRulesState = state.removeRules;
    removeRulesState.error = false;
    removeRulesState.errorTitle = undefined;
    removeRulesState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;
};

export const mutations: MutationTree<DetectionRuleGroupState> = {
    // 検知ルールグループ検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; rule_groups: Array<DetectionRuleGroup> }) {
        state.ruleGroupCount = payload.count;
        state.ruleGroups = payload.rule_groups;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 検知ルールグループ作成系
    [types.CLEAR_ERROR_RULE_GROUP](state) {
        resetErrors(state);
        const createState = state.create;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        resetErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // 検知ルールグループ詳細情報参照系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { group: DetectionRuleGroup; rules: Array<DetectionRule> }) {
        const showState = state.show;
        state.ruleGroupDetails.ruleGroup = payload.group;
        state.ruleGroupDetails.rules = payload.rules.length > 0 ? payload.rules : [];
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 検知ルールグループ更新系
    [types.UPDATE_START](state) {
        resetErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // 検知ルールグループ削除系
    [types.DESTROY_START](state) {
        resetErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // 所属ルール追加系
    [types.CLEAR_ERROR_RULE](state) {
        const addRulesState = state.addRules;
        addRulesState.error = false;
        addRulesState.errorTitle = undefined;
        addRulesState.errorDetails = undefined;
    },

    [types.ADD_RULES_START](state) {
        resetErrors(state);
        const addRulesState = state.addRules;
        addRulesState.isProcessing = true;
    },

    [types.ADD_RULES_SUCCESS](state) {
        const addRulesState = state.addRules;
        addRulesState.isProcessing = false;
    },

    [types.ADD_RULES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addRulesState = state.addRules;
        addRulesState.errorTitle = payload.errorTitle;
        addRulesState.errorDetails = payload.errorDetails;
        addRulesState.error = true;
        addRulesState.isProcessing = false;
    },

    // 所属ルール削減系
    [types.REMOVE_RULES_START](state) {
        resetErrors(state);
        const removeRulesState = state.removeRules;
        removeRulesState.isProcessing = true;
    },

    [types.REMOVE_RULES_SUCCESS](state) {
        const removeRulesState = state.removeRules;
        removeRulesState.isProcessing = false;
    },

    [types.REMOVE_RULES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeRulesState = state.removeRules;
        removeRulesState.errorTitle = payload.errorTitle;
        removeRulesState.errorDetails = payload.errorDetails;
        removeRulesState.error = true;
        removeRulesState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const addRulesState = state.addRules;
        addRulesState.isProcessing = false;

        const removeRulesState = state.removeRules;
        removeRulesState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    // 詳細リセット
    [types.DETAIL_RESET](state) {
        state.ruleGroupDetails.ruleGroup = undefined;
        state.ruleGroupDetails.rules = [];
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.ruleGroups = undefined;
        state.ruleGroupDetails.ruleGroup = undefined;
        state.ruleGroupDetails.rules = [];
        state.ruleGroupCount = 0;
    },
};
